import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Star,
  FileText,
  Edit,
  Trash2,
  ArrowLeft,
  Plus,
  Calendar,
  Clock,
  CheckCircle2,
  MessageSquare,
  Users,
  Briefcase,
  History,
  DollarSign,
  Archive,
  MapPinIcon,
  FolderIcon,
} from "lucide-react";
import { EditSubcontractorSheet } from "./EditSubcontractorSheet";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { EmptyState } from "@/components/ui/empty-state";

interface Subcontractor {
  _id: Id<"subcontractors">;
  name: string;
  logo?: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  contactName: string;
  phone: string;
  email: string;
  services: string[];
  status: "active" | "inactive";
  createdAt: number;
}

interface Note {
  id: string;
  content: string;
  createdAt: string;
  createdBy: string;
}

export default function SubcontractorView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [newNote, setNewNote] = useState("");
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(location.state?.openEditSheet || false);

  // Validate ID parameter
  const isValidId = id && id !== "undefined" && id.trim() !== "";

  // Fetch subcontractor data from Convex
  const subcontractor = useQuery(
    api.subcontractors.getSubcontractorById,
    isValidId ? { id: id as Id<"subcontractors"> } : "skip"
  );

  // Fetch related data
  const notes =
    useQuery(
      api.subcontractors.getSubcontractorNotes,
      isValidId ? { subcontractorId: id as Id<"subcontractors"> } : "skip"
    ) ?? [];

  // Fetch subcontractor service locations
  const subcontractorServiceLocations = useQuery(
    api.subcontractors.getSubcontractorServiceLocations,
    isValidId ? { subcontractorId: id as Id<"subcontractors"> } : "skip"
  );

  // Fetch subcontractor projects
  const subcontractorProjects = useQuery(
    api.subcontractors.getSubcontractorProjects,
    isValidId ? { subcontractorId: id as Id<"subcontractors"> } : "skip"
  );

  const addNoteMutation = useMutation(api.subcontractors.addNote);

  // Archive mutation
  const archiveSubcontractor = useMutation(api.subcontractors.archive);

  // Update mutation
  const updateSubcontractor = useMutation(api.subcontractors.update);

  // Handle archive subcontractor
  const handleArchive = async () => {
    if (!subcontractor) return;

    try {
      await archiveSubcontractor({ id: subcontractor._id });
      toast.success(`${subcontractor.name} has been archived successfully`);
      navigate("/subcontractors");
    } catch (error) {
      console.error("Failed to archive subcontractor:", error);
      toast.error("Failed to archive subcontractor");
    }
  };

  // Handle update subcontractor
  const handleUpdateSubcontractor = async (updatedData: any) => {
    if (!subcontractor) return;

    try {
      await updateSubcontractor({
        id: subcontractor._id,
        name: updatedData.name,
        contactName: updatedData.contactName,
        email: updatedData.email,
        phone: updatedData.phone,
        website: updatedData.website,
        address: updatedData.address,
        city: updatedData.city,
        state: updatedData.state,
        zipCode: updatedData.zipCode,
        services: updatedData.services,
        status: updatedData.status,
      });
      toast.success(`${updatedData.name} has been updated successfully`);
      setIsEditSheetOpen(false);
    } catch (error) {
      console.error("Failed to update subcontractor:", error);
      toast.error("Failed to update subcontractor");
    }
  };

  // Handle invalid ID
  if (!isValidId) {
    return (
      <DashboardLayout activeTab="subcontractors">
        <div className="flex h-full w-full items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="mb-2 text-xl font-semibold">Invalid Subcontractor ID</h2>
            <p className="mb-4 text-gray-500">The subcontractor ID provided is invalid.</p>
            <Button onClick={() => navigate("/subcontractors")}>Back to Subcontractors</Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Handle loading state
  if (subcontractor === undefined) {
    return (
      <DashboardLayout activeTab="subcontractors">
        <div className="flex h-full w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-700"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Handle subcontractor not found
  if (subcontractor === null) {
    return (
      <DashboardLayout activeTab="subcontractors">
        <div className="flex h-full w-full items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="mb-2 text-xl font-semibold">Subcontractor Not Found</h2>
            <p className="mb-4 text-gray-500">
              The subcontractor you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate("/subcontractors")}>Back to Subcontractors</Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const handleAddNote = async () => {
    if (newNote.trim() && id) {
      try {
        await addNoteMutation({
          subcontractorId: id as Id<"subcontractors">,
          content: newNote.trim(),
        });
        setNewNote("");
      } catch (error) {
        console.error("Failed to add note:", error);
      }
    }
  };

  return (
    <DashboardLayout activeTab="subcontractors">
      <div className="h-full w-full space-y-6">
        <div className="mb-4 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => navigate("/subcontractors")}
          >
            <ArrowLeft className="h-4 w-4" /> Back to Subcontractors
          </Button>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-gray-900">{subcontractor.name}</h1>
              <Badge
                className={`${subcontractor.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
              >
                {subcontractor.status.charAt(0).toUpperCase() + subcontractor.status.slice(1)}
              </Badge>
            </div>
            <div className="mt-1 flex items-center gap-2 text-gray-500">
              <MapPin className="h-4 w-4" />
              <span>
                {subcontractor.city}, {subcontractor.state}
              </span>
            </div>
            <div className="mt-1 text-sm text-gray-500">
              Joined: {new Date(subcontractor.createdAt).toLocaleDateString()}
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" onClick={handleArchive}>
              <Archive className="h-4 w-4" /> Archive
            </Button>

            <Button className="gap-2" onClick={() => setIsEditSheetOpen(true)}>
              <Edit className="h-4 w-4" /> Edit
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Left Column - Details */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Contact Information</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="h-4 w-4" />
                  <span>{subcontractor.contactName}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>{subcontractor.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>{subcontractor.email}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Address</h2>
              <div className="space-y-1">
                <p className="text-gray-600">{subcontractor.address}</p>
                <p className="text-gray-600">
                  {subcontractor.city}, {subcontractor.state} {subcontractor.zipCode}
                </p>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Services Offered</h2>
              <div className="flex flex-wrap gap-2">
                {subcontractor.services.map((service: any, index: any) => (
                  <Badge key={index} variant="secondary">
                    {service}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>

          {/* Right Column - Tabs */}
          <div className="md:col-span-2">
            <Tabs defaultValue="service-locations" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="service-locations" className="gap-2">
                  <MapPinIcon className="h-4 w-4" />
                  Service Locations
                </TabsTrigger>
                <TabsTrigger value="projects" className="gap-2">
                  <FolderIcon className="h-4 w-4" />
                  Projects
                </TabsTrigger>
                <TabsTrigger value="notes" className="gap-2">
                  <MessageSquare className="h-4 w-4" /> Notes
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-2">
                  <History className="h-4 w-4" /> History
                </TabsTrigger>
              </TabsList>

              <TabsContent value="service-locations" className="space-y-6">
                <Card className="p-6">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                    <MapPinIcon className="h-5 w-5" />
                    Service Locations
                  </h3>
                  {subcontractorServiceLocations === undefined ? (
                    <div className="py-8 text-center text-gray-500">
                      Loading service locations...
                    </div>
                  ) : subcontractorServiceLocations.length === 0 ? (
                    <EmptyState
                      icon={MapPinIcon}
                      title="No service locations"
                      description="No service locations assigned to this subcontractor."
                    />
                  ) : (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {subcontractorServiceLocations.map((assignment: any) => (
                          <Card key={assignment._id} className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900">
                                    {assignment.location.name}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {assignment.business.name}
                                  </p>
                                </div>
                                <Badge
                                  variant={
                                    assignment.status === "active"
                                      ? "default"
                                      : assignment.status === "completed"
                                        ? "secondary"
                                        : "outline"
                                  }
                                  className={
                                    assignment.status === "active"
                                      ? "bg-green-100 text-green-800"
                                      : assignment.status === "completed"
                                        ? "bg-gray-100 text-gray-800"
                                        : "bg-yellow-100 text-yellow-800"
                                  }
                                >
                                  {assignment.status.charAt(0).toUpperCase() +
                                    assignment.status.slice(1)}
                                </Badge>
                              </div>

                              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                <div>
                                  <span className="font-medium text-gray-700">
                                    Location Address:
                                  </span>
                                  <p className="text-gray-600">{assignment.location.address}</p>
                                </div>
                                {assignment.location.squareFootage && (
                                  <div>
                                    <span className="font-medium text-gray-700">
                                      Square Footage:
                                    </span>
                                    <p className="text-gray-600">
                                      {assignment.location.squareFootage.toLocaleString()} sq ft
                                    </p>
                                  </div>
                                )}
                                {assignment.location.serviceFrequency && (
                                  <div>
                                    <span className="font-medium text-gray-700">
                                      Service Frequency:
                                    </span>
                                    <p className="text-gray-600">
                                      {assignment.location.serviceFrequency}
                                    </p>
                                  </div>
                                )}
                                {assignment.services && assignment.services.length > 0 && (
                                  <div>
                                    <span className="font-medium text-gray-700">Services:</span>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {assignment.services.map((service: any, index: any) => (
                                        <Badge key={index} variant="secondary" className="text-xs">
                                          {service}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {assignment.location.serviceDays &&
                                assignment.location.serviceDays.length > 0 && (
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">
                                      Service Days:
                                    </span>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {assignment.location.serviceDays.map((day: any) => (
                                        <Badge key={day} variant="secondary" className="text-xs">
                                          {day}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                              <div className="border-t border-gray-100 pt-2">
                                <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                  {assignment.business.contactName && (
                                    <div>
                                      <span className="font-medium text-gray-700">
                                        Client Contact:
                                      </span>
                                      <p className="text-gray-600">
                                        {assignment.business.contactName}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <span className="font-medium text-gray-700">Assigned:</span>
                                    <p className="text-gray-600">
                                      {new Date(assignment.assignedAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="projects" className="space-y-6">
                <Card className="p-6">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                    <FolderIcon className="h-5 w-5" />
                    Projects
                  </h3>
                  {subcontractorProjects === undefined ? (
                    <div className="py-8 text-center text-gray-500">Loading projects...</div>
                  ) : subcontractorProjects.length === 0 ? (
                    <EmptyState
                      icon={FolderIcon}
                      title="No projects"
                      description="No projects assigned to this subcontractor."
                    />
                  ) : (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {subcontractorProjects.map((project: any) => (
                          <Card key={project._id} className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{project.name}</h4>
                                  <p className="text-sm text-gray-600">
                                    {project.location.name} - {project.business.name}
                                  </p>
                                </div>
                                <Badge
                                  variant={
                                    project.status === "Completed"
                                      ? "default"
                                      : project.status === "In Progress"
                                        ? "secondary"
                                        : "outline"
                                  }
                                  className={
                                    project.status === "Completed"
                                      ? "bg-green-100 text-green-800"
                                      : project.status === "In Progress"
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-yellow-100 text-yellow-800"
                                  }
                                >
                                  {project.status}
                                </Badge>
                              </div>

                              {project.description && (
                                <p className="text-sm text-gray-600">{project.description}</p>
                              )}

                              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                <div>
                                  <span className="font-medium text-gray-700">Start Date:</span>
                                  <p className="text-gray-600">
                                    {new Date(project.startDate).toLocaleDateString()}
                                  </p>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Due Date:</span>
                                  <p className="text-gray-600">
                                    {new Date(project.dueDate).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>

                              {project.assignedSubcontractorNames.length > 0 && (
                                <div>
                                  <span className="text-sm font-medium text-gray-700">
                                    Subcontractors:
                                  </span>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {project.assignedSubcontractorNames.map(
                                      (name: any, index: any) => (
                                        <Badge key={index} variant="outline" className="text-xs">
                                          {name}
                                        </Badge>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                              <div className="border-t border-gray-100 pt-2">
                                <div className="text-sm text-gray-600">
                                  <span className="font-medium text-gray-700">Location:</span>{" "}
                                  {project.location.address}
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="notes" className="space-y-6">
                <Card className="p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Notes</h3>
                  </div>
                  <div className="space-y-4">
                    <Card className="p-4">
                      <Textarea
                        placeholder="Add a note about this subcontractor..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="mb-4"
                      />
                      <Button className="gap-2" onClick={handleAddNote}>
                        <Plus className="h-4 w-4" /> Add Note
                      </Button>
                    </Card>
                    <ScrollArea className="h-[300px] pr-4">
                      <div className="space-y-4">
                        {notes.length === 0 ? (
                          <EmptyState
                            icon={MessageSquare}
                            title="No notes"
                            description="No notes for this subcontractor yet."
                          />
                        ) : (
                          notes.map((note: any) => (
                            <Card key={note._id} className="p-4">
                              <div className="space-y-2">
                                <p className="text-gray-600">{note.content}</p>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Users className="h-3 w-3" />
                                  <span>{note.createdBy}</span>
                                  <span>•</span>
                                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                                </div>
                              </div>
                            </Card>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="history" className="space-y-6">
                <Card className="p-6">
                  <h3 className="mb-4 text-lg font-semibold">Activity History</h3>
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-4">
                      <Card className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="rounded-full bg-blue-100 p-2">
                            <Edit className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">Subcontractor information updated</p>
                            <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                              <Users className="h-3 w-3" />
                              <span>Jane Smith</span>
                              <span>•</span>
                              <span>2024-03-25 14:30</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                      <Card className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="rounded-full bg-green-100 p-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">
                              Assigned to project "MediCenter Post-Construction"
                            </p>
                            <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                              <Users className="h-3 w-3" />
                              <span>John Doe</span>
                              <span>•</span>
                              <span>2024-03-01 09:15</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                      <Card className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="rounded-full bg-blue-100 p-2">
                            <Edit className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">Service offerings updated</p>
                            <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                              <Users className="h-3 w-3" />
                              <span>Jane Smith</span>
                              <span>•</span>
                              <span>2024-02-15 11:45</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                      <Card className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="rounded-full bg-green-100 p-2">
                            <Plus className="h-4 w-4 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">Subcontractor added to system</p>
                            <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                              <Users className="h-3 w-3" />
                              <span>John Doe</span>
                              <span>•</span>
                              <span>
                                {new Date(subcontractor.createdAt).toLocaleDateString()} 10:30
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </ScrollArea>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <EditSubcontractorSheet
        isOpen={isEditSheetOpen}
        onClose={() => setIsEditSheetOpen(false)}
        subcontractor={{
          id: subcontractor._id,
          name: subcontractor.name,
          contactName: subcontractor.contactName,
          email: subcontractor.email,
          phone: subcontractor.phone,
          address: subcontractor.address,
          city: subcontractor.city,
          state: subcontractor.state,
          zipCode: subcontractor.zipCode,
          website: subcontractor.website || "",
          services: subcontractor.services,
          notes: "",
          status: subcontractor.status,
        }}
        onSave={handleUpdateSubcontractor}
      />
    </DashboardLayout>
  );
}
